import { useWeb3React } from '@web3-react/core';
import useSWR from 'swr';
import useTradePools from './useTradePools';
import { verifyChainConfig, subgraphQuery } from '../../../helpers';
import { Trader } from '../../../config/constants';

const useUserPositionIds = () => {
    const { chainId, account } = useWeb3React();

    const { tradePools } = useTradePools();

    const { data, error, mutate } = useSWR(
        chainId && account && tradePools
            ? ['positionIdsByTradePair', chainId, account, tradePools]
            : null,
        async (_, chainId, userAddress, tradePools) => {
            if (!verifyChainConfig(chainId)) return;

            //  fetch all positions using loop
            const query = `
                  traders(where: {user: "${userAddress.toLowerCase()}"}) {
                    positions(where: {status_not_in: ["CLOSED", "LIQUIDATED"]}) {
                        id
                        entryMargin
                        openedTimestamp
                        tradePair {
                            id
                        }
                    }
                  }
            `;

            const data: { traders: Pick<Trader, 'positions'>[] } = await subgraphQuery(
                query,
                { chainId }
            );

            const subgraphUserPositions = data.traders?.[0]?.positions ?? [];

            const positionIdsByTradePair = tradePools.reduce((acc, { address }) => {
                return {
                    ...acc,
                    [address.toLowerCase()]: [],
                };
            }, {} as Record<string, string[]>);

            subgraphUserPositions.forEach(({ id, tradePair }) => {
                if (
                    !Object.keys(positionIdsByTradePair).includes(
                        tradePair.id.toLowerCase()
                    )
                )
                    return;

                positionIdsByTradePair[tradePair.id.toLowerCase()].push(id.split('-')[1]);
            });

            return positionIdsByTradePair;
        }
    );

    const addUserPositionId = async (tradePair: string, id: string) => {
        const tradePairId = tradePair.toLowerCase();

        await mutate(
            (prevData) => {
                if (!prevData) return;

                const existingId = prevData[tradePairId].includes(id.toString());

                if (existingId) return prevData;

                return {
                    ...prevData,
                    [tradePairId]: [...prevData[tradePairId], id.toString()],
                };
            },
            { populateCache: true, revalidate: false }
        );
    };

    const removeUserPositionId = async (tradePair: string, id: string) => {
        const tradePairId = tradePair.toLowerCase();

        await mutate(
            (prevData) => {
                if (!prevData) return;

                const existingId = prevData[tradePairId].includes(id);

                if (!existingId) return prevData;

                return {
                    ...prevData,
                    [tradePairId]: prevData[tradePairId].filter(
                        (positionId) => positionId !== id
                    ),
                };
            },
            { populateCache: true, revalidate: false }
        );
    };

    return {
        userPositionIds: data,
        isLoading: !data && !error,
        error,
        updateUserPositionIds: mutate,
        addUserPositionId,
        removeUserPositionId,
    };
};

export default useUserPositionIds;
